<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="comm-crop-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="comm-crop-title"
      @click.self="onCancel"
    >
      <div class="comm-crop-dialog">
        <h3 id="comm-crop-title" class="comm-crop-title">Ajustar imagem</h3>
        <p class="comm-crop-hint">{{ displayHint }}</p>
        <div class="comm-crop-stage">
          <img
            v-if="open && imageSrc"
            ref="cropImg"
            :src="imageSrc"
            alt=""
            class="comm-crop-img"
            @load="onImageLoaded"
          />
        </div>
        <div class="comm-crop-actions">
          <button type="button" class="comm-crop-btn ghost" @click="onCancel">Cancelar</button>
          <button type="button" class="comm-crop-btn primary" :disabled="busy || !cropper" @click="onApply">
            {{ busy ? "A processar…" : "Usar imagem" }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";

export default {
  name: "CommunityImageCropModal",
  props: {
    open: { type: Boolean, default: false },
    imageSrc: { type: String, default: "" },
    /** Proporção fixa do recorte (ex.: 4/3 para posts, 1 para avatar). */
    aspectRatio: { type: Number, default: 4 / 3 },
    maxCropWidth: { type: Number, default: 1600 },
    maxCropHeight: { type: Number, default: 1200 },
    /** Se vazio, usa texto padrão conforme a proporção. */
    hintText: { type: String, default: "" },
  },
  emits: ["close", "confirm"],
  data() {
    return { cropper: null, busy: false };
  },
  computed: {
    displayHint() {
      if (this.hintText) return this.hintText;
      const r = this.aspectRatio;
      if (r >= 0.98 && r <= 1.02) {
        return "Área quadrada 1:1 — enquadre o rosto; arraste e ajuste o quadro antes de usar.";
      }
      return "Área fixa 4:3 — arraste a foto e redimensione o quadro antes de usar.";
    },
  },
  watch: {
    open(val) {
      if (val) {
        this.busy = false;
        this.$nextTick(() => {
          const el = this.$refs.cropImg;
          if (el?.complete && el.naturalWidth > 0) this.mountCropper();
        });
      } else {
        this.teardown();
      }
    },
  },
  beforeUnmount() {
    this.teardown();
  },
  methods: {
    onImageLoaded() {
      if (!this.open) return;
      this.$nextTick(() => this.mountCropper());
    },
    mountCropper() {
      this.teardown();
      const el = this.$refs.cropImg;
      if (!el || !this.imageSrc) return;
      this.cropper = new Cropper(el, {
        aspectRatio: this.aspectRatio,
        viewMode: 1,
        dragMode: "move",
        autoCropArea: 0.88,
        restore: false,
        guides: true,
        center: true,
        highlight: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        minContainerWidth: 280,
        minContainerHeight: 220,
      });
    },
    teardown() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    onCancel() {
      this.$emit("close");
    },
    onApply() {
      if (!this.cropper) return;
      this.busy = true;
      const canvas = this.cropper.getCroppedCanvas({
        maxWidth: this.maxCropWidth,
        maxHeight: this.maxCropHeight,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });
      if (!canvas) {
        this.busy = false;
        return;
      }
      canvas.toBlob(
        (blob) => {
          this.busy = false;
          if (blob) this.$emit("confirm", blob);
          this.$emit("close");
        },
        "image/jpeg",
        0.92,
      );
    },
  },
};
</script>

<style scoped>
.comm-crop-backdrop {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.comm-crop-dialog {
  background: #fff;
  border-radius: 16px;
  max-width: min(920px, 100%);
  width: 100%;
  max-height: min(92vh, 900px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.18);
}
.comm-crop-title {
  margin: 0;
  padding: 18px 20px 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
}
.comm-crop-hint {
  margin: 6px 20px 12px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
}
.comm-crop-stage {
  flex: 1;
  min-height: 280px;
  max-height: 60vh;
  background: #0f172a;
  margin: 0 12px;
  border-radius: 12px;
  overflow: hidden;
}
.comm-crop-img {
  display: block;
  max-width: 100%;
}
.comm-crop-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px 18px;
  border-top: 1px solid #e2e8f0;
}
.comm-crop-btn {
  border-radius: 10px;
  padding: 10px 18px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  border: none;
  font-family: inherit;
}
.comm-crop-btn.ghost {
  background: #f1f5f9;
  color: #475569;
}
.comm-crop-btn.primary {
  background: #004ac6;
  color: #fff;
}
.comm-crop-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
